/**
 * Application api implementation
 */

import axios from 'axios';
import { NextcloudThemingCapabilities } from '@sendent/nextcloud-api';
import { store } from '../store';
import { LocalStorageHelper, Crypto, logger } from '../helpers';
import {
    parseReleaseNotes,
    RELEASE_NOTES_PATH,
} from '../helpers/release-notes';
import type { ReleaseEntry } from '../helpers/release-notes';

export enum AppErrorCode {
    CustomError = 'CustomError',
}

/**
 * Load config
 */
const getConfig = async () => {
    logger.info('getConfig is called', 'API');
    const state = store.getState();
    if (state.appReducer.appConfig.appName.length <= 0) {
        return (await axios.get('assets/config.json')).data;
    }
    return state.appReducer.appConfig;
};

/**
 * Load settings config
 */
const getSettingsConfig = async () => {
    logger.info('getSettingsConfig is called', 'API');
    return (await axios.get('assets/settings.json')).data;
};

/**
 * Load release notes
 */
const getReleaseNotes = async (): Promise<ReleaseEntry[]> => {
    try {
        const data = (await axios.get(RELEASE_NOTES_PATH)).data;
        return parseReleaseNotes(data);
    } catch (e) {
        logger.debug(`No release notes found: ${(e as Error).message}`, 'API');
        return [];
    }
};

function getCacheUserConfiguration() {
    try {
        const state = store.getState();
        if (
            LocalStorageHelper.storeContainsKey(
                LocalStorageHelper.storeKeys.CACHE_USER_CONFIGURATION,
            )
        ) {
            const userCache = JSON.parse(
                Crypto.decrypt(
                    LocalStorageHelper.storeGetObject(
                        LocalStorageHelper.storeKeys.CACHE_USER_CONFIGURATION,
                    ) ?? '',
                ),
            );
            const dateAdded = new Date(userCache.dateAdded);
            dateAdded.setMinutes(
                dateAdded.getMinutes() + state.appReducer.appConfig.cacheTime,
            );
            const dt = new Date();
            if (dateAdded.getTime() >= dt.getTime()) {
                return userCache;
            }
        }
    } catch (ex) {
        logger.error(
            `getCacheUserConfiguration error: ${(ex as Error).message}`,
            'Config',
        );
    }
    return null;
}

// Only the theming is looked at here, the rest is cached as it comes in.
type UserConfigCache = {
    nextcloudTheming?: NextcloudThemingCapabilities | null;
};

function writeUserCache(userCache: UserConfigCache): boolean {
    return LocalStorageHelper.storeSetObject(
        LocalStorageHelper.storeKeys.CACHE_USER_CONFIGURATION,
        Crypto.encrypt(JSON.stringify(userCache)),
    );
}

function setCacheUserConfiguration(userCache: UserConfigCache) {
    try {
        if (writeUserCache(userCache)) {
            return;
        }
        // Too large for the storage quota: drop the theming rather than cache
        // nothing — loadUserConfiguration re-fetches it when it's missing.
        if (!userCache.nextcloudTheming) {
            return;
        }
        const stored = writeUserCache({
            ...userCache,
            nextcloudTheming: null,
        });
        if (stored) {
            logger.info(
                'Cached user configuration without the theming',
                'Config',
            );
        }
    } catch (ex) {
        logger.error(
            `setCacheUserConfiguration error: ${(ex as Error).message}`,
            'Config',
        );
    }
}

function clearCacheUserConfiguration() {
    try {
        if (
            LocalStorageHelper.storeContainsKey(
                LocalStorageHelper.storeKeys.CACHE_USER_CONFIGURATION,
            )
        ) {
            LocalStorageHelper.storeRemoveKey(
                LocalStorageHelper.storeKeys.CACHE_USER_CONFIGURATION,
            );
        }
    } catch (ex) {
        logger.error(
            `clearCacheUserConfiguration error: ${(ex as Error).message}`,
            'Config',
        );
    }
}

type DefaultNextcloudUrlData = {
    defaultNextcloudUrl: string | null;
};

const getDefaultNextcloudUrl = async (): Promise<string | null> => {
    try {
        const res = await axios.get<DefaultNextcloudUrlData>(
            '/api/default-nextcloud-url',
        );

        return res.data.defaultNextcloudUrl;
    } catch (err) {
        logger.error(
            `getDefaultNextcloudUrl error: ${
                err instanceof Error ? err.message : 'unknown'
            }`,
            'API',
        );

        return null;
    }
};

export const appApi = {
    getConfig,
    getSettingsConfig,
    getReleaseNotes,
    getCacheUserConfiguration,
    setCacheUserConfiguration,
    clearCacheUserConfiguration,
    getDefaultNextcloudUrl,
};
